import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateSelectedFloor,
  updateSelectedZones,
  addExcludedDepartment,
  removeExcludedDepartment,
} from "../../../state/reducers";

export const FilterControls = () => {
  const dispatch = useDispatch();
  const { selectedFloor, excludedDepartments, selectedZones, allFloors, allZones, allDepartments } =
    useSelector((state) => state.filters);
  const { loading, dailyData, weeklyData, monthlyData, customData } = useSelector((state) => state.utilizationData);
  const { dateRangeType } = useSelector((state) => state.dateRange);

  const [filterBy, setFilterBy] = useState("zone");
  const [tempZone, setTempZone] = useState("All Zones");
  const [showZoneDropdown, setShowZoneDropdown] = useState(false);
  const [zoneSearchTerm, setZoneSearchTerm] = useState("");
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState("");
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);

  const zoneDropdownRef = useRef(null);
  const departmentDropdownRef = useRef(null);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (zoneDropdownRef.current && !zoneDropdownRef.current.contains(event.target)) {
        setShowZoneDropdown(false);
      }
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target)) {
        setShowDepartmentDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get floor capacity from current data
  const currentData =
    dateRangeType === "weekly"
      ? weeklyData
      : dateRangeType === "monthly"
      ? monthlyData
      : dateRangeType === "custom"
      ? customData
      : dailyData;
  // Calculate capacity based on selected floor
  const floorsData = currentData.find((item) => item.type === "Floors")?.data || [];
  const floorCapacity = selectedFloor === "all"
    ? floorsData.reduce((sum, floor) => sum + (floor.capacity || 0), 0)
    : floorsData.find(floor => floor.floorName === selectedFloor)?.capacity || 0;

  // Get selected departments (all departments minus excluded)
  const selectedDepartments = allDepartments.filter((dept) => !excludedDepartments.includes(dept));

  // Sync temp zone with selected zones
  useEffect(() => {
    if (selectedZones.length === 1) {
      setTempZone(selectedZones[0]);
    } else {
      setTempZone("All Zones");
    }
  }, [selectedZones]);

  const handleFloorSelection = useCallback(
    (floor) => {
      dispatch(updateSelectedFloor(floor));

      // Auto-select appropriate zones based on floor
      if (floor === "8th Floor") {
        dispatch(updateSelectedZones(["MDR"]));
        setTempZone("MDR");
      } else if (floor === "9th Floor") {
        const zones = allZones.filter(
          (z) =>
            !z.includes("Overflow (10F)") &&
            !z.includes("Overflow (Combined)")
        );
        dispatch(updateSelectedZones(zones));
        setTempZone("All Zones");
      } else if (floor === "10th Floor") {
        const zones = allZones.filter(
          (z) =>
            !z.includes("Overflow (9F)") && !z.includes("Overflow (Combined)")
        );
        dispatch(updateSelectedZones(zones));
        setTempZone("All Zones");
      } else if (floor === "all") {
        const zones = allZones.filter(
          (z) =>
            !z.includes("Overflow (9F)") && !z.includes("Overflow (10F)")
        );
        dispatch(updateSelectedZones(zones));
        setTempZone("All Zones");
      } else {
        dispatch(updateSelectedZones(allZones));
        setTempZone("All Zones");
      }
    },
    [dispatch, allZones]
  );

  const handleFilterChange = useCallback((filter) => {
    setFilterBy(filter);
    if (filter === "department") {
      setZoneSearchTerm("");
    } else if (filter === "zone") {
      setDepartmentSearchTerm("");
    }
  }, []);

  const handleZoneSubmit = () => {
    if (tempZone === "All Zones") {
      dispatch(updateSelectedZones([]));
    } else {
      dispatch(updateSelectedZones([tempZone]));
    }
    setShowZoneDropdown(false);
  };

  const handleAddDepartment = (dept) => {
    dispatch(removeExcludedDepartment(dept));
    setShowDepartmentDropdown(false);
  };

  const handleRemoveDepartment = (dept) => {
    dispatch(addExcludedDepartment(dept));
  };

  const handleResetDepartments = () => {
    // Clear all excluded departments
    excludedDepartments.forEach((dept) => {
      dispatch(removeExcludedDepartment(dept));
    });
  };

  // Get zones available for the selected floor
  const getAvailableZones = () => {
    if (selectedFloor === "8th Floor") {
      return allZones.filter((z) => z === "MDR");
    } else if (selectedFloor === "9th Floor") {
      return allZones.filter(
        (z) =>
          !z.includes("Overflow (10F)") &&
          !z.includes("Overflow (Combined)")
      );
    } else if (selectedFloor === "10th Floor") {
      return allZones.filter(
        (z) =>
          !z.includes("Overflow (9F)") && !z.includes("Overflow (Combined)")
      );
    } else if (selectedFloor === "all") {
      return allZones.filter(
        (z) =>
          !z.includes("Overflow (9F)") && !z.includes("Overflow (10F)")
      );
    }
    return allZones;
  };

  const availableZones = getAvailableZones();
  const filteredZones = availableZones.filter((zone) =>
    zone.toLowerCase().includes(zoneSearchTerm.toLowerCase())
  );

  const filteredExcludedDepartments = excludedDepartments.filter((dept) =>
    dept.toLowerCase().includes(departmentSearchTerm.toLowerCase())
  );

  return (
    <div className="row g-3">
      {/* Floor Selection */}
      <div className="col-md-6">
        <label className="form-label">Floor</label>
        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn btn-sm ${selectedFloor === "all" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => handleFloorSelection("all")}
            disabled={loading}
          >
            {selectedFloor === "all" ? `All (${floorCapacity})` : "All"}
          </button>
          {allFloors.slice(0, 3).map((floor) => (
            <button
              key={floor}
              type="button"
              className={`btn btn-sm ${selectedFloor === floor ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => handleFloorSelection(floor)}
              disabled={loading}
            >
              {selectedFloor === floor ? `${floor} (${floorCapacity})` : floor}
            </button>
          ))}
        </div>
      </div>

      {/* Zone/Department Filter */}
      <div className="col-md-6">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label mb-0">
            {filterBy === "department" ? "Departments" : "Zones"}
          </label>
          <div className="btn-group btn-group-sm" role="group">
            <span className="me-2 text-muted" style={{ lineHeight: "1.9" }}>Filter by:</span>
            <button
              type="button"
              className={`btn ${filterBy === "department" ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => handleFilterChange("department")}
            >
              Department
            </button>
            <button
              type="button"
              className={`btn ${filterBy === "zone" ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => handleFilterChange("zone")}
            >
              Zone
            </button>
          </div>
        </div>

        {filterBy === "zone" ? (
          <div>
            {/* Zone Pills Display */}
            <div
              className="border rounded p-2 mb-2"
              style={{ minHeight: "80px", backgroundColor: "#f8f9fa" }}
            >
              {tempZone && tempZone !== "All Zones" ? (
                <span className="badge bg-primary rounded-pill me-2">{tempZone}</span>
              ) : (
                <small className="text-muted">All Zones selected</small>
              )}
            </div>

            {/* Zone Dropdown */}
            <div className="dropdown" ref={zoneDropdownRef}>
              <button
                className="btn btn-sm btn-outline-secondary w-100 dropdown-toggle"
                type="button"
                onClick={() => setShowZoneDropdown(!showZoneDropdown)}
                disabled={loading}
              >
                Selected: {tempZone}
              </button>
              {showZoneDropdown && (
                <div className="dropdown-menu show w-100 p-3" style={{ maxHeight: "400px", overflowY: "hidden" }}>
                  <input
                    type="text"
                    className="form-control form-control-sm mb-3"
                    placeholder="Search zones..."
                    value={zoneSearchTerm}
                    onChange={(e) => setZoneSearchTerm(e.target.value)}
                  />
                  <div style={{ maxHeight: "250px", overflowY: "auto" }} className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="zoneRadio"
                        id="zone-all"
                        checked={tempZone === "All Zones"}
                        onChange={() => setTempZone("All Zones")}
                      />
                      <label className="form-check-label" htmlFor="zone-all">
                        All Zones
                      </label>
                    </div>
                    {filteredZones.map((zone) => (
                      <div className="form-check" key={zone}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="zoneRadio"
                          id={`zone-${zone}`}
                          checked={tempZone === zone}
                          onChange={() => setTempZone(zone)}
                        />
                        <label className="form-check-label" htmlFor={`zone-${zone}`}>
                          {zone}
                        </label>
                      </div>
                    ))}
                  </div>
                  <button
                    className="btn btn-sm btn-primary w-100"
                    onClick={handleZoneSubmit}
                  >
                    Submit
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            {/* Department Pills Display */}
            <div
              className="border rounded p-2 mb-2"
              style={{ minHeight: "80px", backgroundColor: "#f8f9fa" }}
            >
              {selectedDepartments.length > 0 ? (
                selectedDepartments.map((dept) => (
                  <span key={dept} className="badge bg-primary rounded-pill me-2 mb-1">
                    {dept}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-1"
                      style={{ fontSize: "0.5rem" }}
                      onClick={() => handleRemoveDepartment(dept)}
                      disabled={loading}
                    />
                  </span>
                ))
              ) : (
                <small className="text-muted">No departments selected</small>
              )}
            </div>

            {/* Add Department Dropdown */}
            {excludedDepartments.length > 0 ? (
              <div className="dropdown mb-2" ref={departmentDropdownRef}>
                <button
                  className="btn btn-sm btn-outline-secondary w-100 dropdown-toggle"
                  type="button"
                  onClick={() => setShowDepartmentDropdown(!showDepartmentDropdown)}
                  disabled={loading}
                >
                  Add departments ({excludedDepartments.length})
                </button>
                {showDepartmentDropdown && (
                  <div className="dropdown-menu show w-100 p-3" style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm mb-2"
                      placeholder="Search departments..."
                      value={departmentSearchTerm}
                      onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                    />
                    {filteredExcludedDepartments.length > 0 ? (
                      filteredExcludedDepartments.map((dept) => (
                        <button
                          key={dept}
                          className="dropdown-item"
                          onClick={() => handleAddDepartment(dept)}
                        >
                          {dept}
                        </button>
                      ))
                    ) : (
                      <div className="text-center py-2">
                        <small className="text-muted">No departments found</small>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-2">
                <small className="text-muted">All departments are selected</small>
              </div>
            )}

            {/* Apply/Reset buttons for department filter */}
            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={handleResetDepartments}
                disabled={loading || excludedDepartments.length === 0}
              >
                Reset
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
